import { ThemeEvents } from '@theme/events';

/**
 * <amazon-fallback> custom element.
 *
 * Listens to the theme's `variant:update` event (dispatched by variant-picker.js
 * after a fetch completes) and shows or hides the Amazon CTA based on:
 *   1. Whether the selected variant is out of stock (resource.available === false)
 *   2. Whether the selected variant has `custom.amazon_url` metafield value
 *
 * If in stock OR metafield URL is blank, the element is hidden.
 * No duplicate inventory logic — reuses the same event as product-form.js.
 */
class AmazonFallback extends HTMLElement {
  connectedCallback() {
    // Attach to the closest section container, matching how product-form.js
    // and product-inventory.js scope their listeners.
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * Hides the element consistently — sets both [hidden] and inline style so
   * neither CSS nor a missing stylesheet can accidentally reveal it.
   */
  #hide() {
    this.setAttribute('hidden', '');
    this.style.display = 'none';
  }

  /**
   * Handles variant:update events.
   * Mirrors the structure used by product-form.js #onVariantUpdate.
   *
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail.resource;
    const targetElement = event.target;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (targetElement instanceof HTMLElement && targetElement.dataset.productId !== this.dataset.productId) {
      return;
    }

    // No variant determined (e.g. indeterminate combined listing state) — hide
    if (!variant) {
      this.#hide();
      return;
    }

    // Variant is available — hide Amazon CTA and let normal ATC flow continue
    if (variant.available) {
      this.#hide();
      return;
    }

    // Variant metafield URLs are rendered in the fetched product HTML.
    // Read URL from the new amazon-fallback node to keep this logic minimal and
    // aligned with the theme's existing variant:update server-render flow.
    const newFallback = event.detail.data.html.querySelector('amazon-fallback');
    const amazonUrl = newFallback?.dataset.amazonUrl || '';

    if (!amazonUrl) {
      // Variant metafield is empty — hide container + button
      this.#hide();
      return;
    }

    // Match found — update button href and reveal the CTA.
    // Also clear the inline style="display:none" that Liquid sets as a
    // belt-and-suspenders guard on the initial server render.
    const button = this.querySelector('.amazon-fallback__button');
    if (button) {
      button.href = amazonUrl;
    }
    this.dataset.amazonUrl = amazonUrl;

    this.style.removeProperty('display');
    this.removeAttribute('hidden');
  };
}

if (!customElements.get('amazon-fallback')) {
  customElements.define('amazon-fallback', AmazonFallback);
}
